import { FloatingLabelProps, FloatingLabel, Form, Stack } from 'react-bootstrap';
import { useFormContext, Controller, FieldValues, Path } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { removeNumbers, stripFormatting } from './FormHelpers';
import { useWizardFormContext } from '@odmonorepo/core-components';
import { ReactNode } from 'react';
import { trackField } from 'merchant-components';

interface FormInputProps<TFieldValues extends FieldValues> extends Omit<FloatingLabelProps, 'label'> {
  name: Path<TFieldValues>;
  required?: boolean;
  type?: string;
  disabled?: boolean;
  label?: ReactNode;
  useYupErrors?: boolean;
  helperText?: ReactNode;
  setValueAsNumber?: boolean;
  formatValue?: (value: any) => any;
  stripFormatOnSave?: boolean;
  controlProps?: any;
  labelTooltip?: ReactNode;
}

export const FormInput = <TFieldValues extends FieldValues>({
  name,
  required = true,
  type = 'text',
  label,
  disabled = false,
  useYupErrors = false,
  helperText,
  setValueAsNumber = false,
  formatValue,
  stripFormatOnSave = false,
  controlProps,
  labelTooltip,
  ...props
}: FormInputProps<TFieldValues>) => {
  const { control, setValue, clearErrors, trigger, formState } = useFormContext<TFieldValues>();
  const { formName } = useWizardFormContext() || { formName: '' };
  const labelContent = label || <FormattedMessage id={'formInput.' + removeNumbers(name)} />;
  const fieldError = (name as string)?.split('.').reduce<any>((o, k) => (o || {})[k], formState?.errors);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, onBlur, value } }) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const val = formatValue ? formatValue(e.target.value) : e.target.value;
          const rawValue = stripFormatOnSave ? stripFormatting(val) : val;
          const finalValue = setValueAsNumber && rawValue ? Number(rawValue) : rawValue;
          setValue(name, finalValue as any);
          clearErrors(name);
          onChange(finalValue)
        };

        return (
          <FloatingLabel
            controlId={name}
            {...props}
            label={
              labelTooltip ? (
                <span
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{ pointerEvents: 'auto' }}
                  >
                  <Stack direction="horizontal" className="text-start">
                    {labelContent}
                    {labelTooltip}
                  </Stack>
                </span>
              ) : labelContent
            }
          >
            <Form.Control
              value={formatValue ? formatValue(value || '') : value || ''}
              onWheel={e => e.currentTarget.blur()}
              type={type}
              onChange={handleChange}
              onBlur={() => {
                trackField(
                  formName,
                  name as string,
                  'Blur'
                );
                onBlur();
              }}
              required={required}
              disabled={disabled}
              isInvalid={!!fieldError}
              form={formName || ''}
              {...controlProps}
              data-testid={`${props['data-testid']}Input`}
            />
            {helperText && (
              <div className="text-gray fs-8 mt-1">
                {typeof helperText === 'boolean' ? <FormattedMessage id={'formInput.' + removeNumbers(name) + '.helperText'} /> : helperText}
              </div>
            )}
            <Form.Control.Feedback type="invalid">
              {useYupErrors && fieldError?.message
                ? (
                    <FormattedMessage
                      id={fieldError.message}
                      defaultMessage={fieldError.message}
                    />
                ) : (
                    <FormattedMessage id={'formInput.' + removeNumbers(name) + '.error'} />
                )
              }
            </Form.Control.Feedback>
          </FloatingLabel>
        );
      }}
    />
  );
};

export default FormInput;